import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Briefcase, GraduationCap, BookOpen, Clock, FileText, Target,
  TrendingUp, Award, Sparkles, ArrowRight, RefreshCw, Cloud, Cpu, Landmark
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Briefcase,
  GraduationCap,
  BookOpen,
  Cloud,
  Cpu,
  Landmark,
};

export default function ExamPrep() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [exams, setExams] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catalogRes, suggestedRes, progressRes] = await Promise.all([
        axios.get(`${API}/exam-prep/catalog`),
        token
          ? axios.get(`${API}/exam-prep/suggested-exams`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : Promise.resolve({ data: null }),
        token
          ? axios.get(`${API}/exam-prep/progress`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : Promise.resolve({ data: { exams: [] } }),
      ]);
      setExams(catalogRes.data.exams || []);
      setSuggested(suggestedRes.data);
      setProgress(progressRes.data?.exams || []);
    } catch (err) {
      console.error("Failed to load exam prep data:", err);
      toast.error("Failed to load exams. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const getProgressForExam = (examId) =>
    progress.find((p) => p.exam_id === examId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8" data-testid="exam-prep-header">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Exam Preparation Hub
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                AI-powered prep for competitive & government exams with personalized study plans
              </p>
            </div>
          </div>
        </div>

        {/* Suggested (Career Twin integration) */}
        {suggested?.personalized && (
          <Card
            className="mb-6 border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-l-purple-500"
            data-testid="suggested-exams-card"
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Personalized for You
              </CardTitle>
              <CardDescription>{suggested.reason}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Progress Summary */}
        {progress.length > 0 && (
          <Card className="mb-6 border-0 shadow-md" data-testid="progress-summary-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {progress.map((p) => (
                  <div
                    key={p.exam_id}
                    className="bg-gradient-to-br from-white to-blue-50 p-4 rounded-lg border border-blue-100"
                  >
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {p.exam_name}
                    </p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold text-blue-600">
                        {p.accuracy_percent}%
                      </span>
                      <span className="text-xs text-gray-500">accuracy</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {p.practice_sessions} practice • {p.mock_tests} mocks •{" "}
                      {p.total_questions} Qs
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Exam Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {exams.map((exam) => {
            const Icon = iconMap[exam.category_icon] || BookOpen;
            const examProgress = getProgressForExam(exam.exam_id);
            return (
              <Card
                key={exam.exam_id}
                className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden bg-white"
                data-testid={`exam-card-${exam.exam_id}`}
              >
                <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{exam.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {exam.category}
                        </Badge>
                      </div>
                    </div>
                    <Badge
                      className={`text-xs ${
                        exam.difficulty === "Easy"
                          ? "bg-green-100 text-green-800"
                          : exam.difficulty === "Hard"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {exam.difficulty}
                    </Badge>
                  </div>
                  <CardDescription className="mt-3 line-clamp-2">
                    {exam.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                    <Stat
                      icon={<FileText className="w-3 h-3" />}
                      label="Questions"
                      value={exam.total_questions}
                    />
                    <Stat
                      icon={<Clock className="w-3 h-3" />}
                      label="Duration"
                      value={`${exam.total_duration_minutes}m`}
                    />
                    <Stat
                      icon={<BookOpen className="w-3 h-3" />}
                      label="Topics"
                      value={exam.topic_count}
                    />
                  </div>

                  {examProgress && (
                    <div className="bg-blue-50 p-2 rounded-md text-xs mb-3 flex items-center justify-between">
                      <span className="text-gray-700">Your accuracy</span>
                      <span className="font-bold text-blue-700">
                        {examProgress.accuracy_percent}% ({examProgress.total_questions} Qs)
                      </span>
                    </div>
                  )}

                  <Button
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 group"
                    onClick={() => navigate(`/exam-prep/${exam.exam_id}`)}
                    data-testid={`start-exam-btn-${exam.exam_id}`}
                  >
                    {examProgress ? "Continue Preparation" : "Start Preparation"}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer tips */}
        <Card className="mt-8 border-0 shadow-sm bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-0.5 text-amber-800">
                  <li>Pick an exam → browse topics → start topic-wise practice (AI-generated questions)</li>
                  <li>Take full-length timed mock tests that match the actual exam pattern</li>
                  <li>Get an AI-generated personalized study plan based on your target date</li>
                  <li>Stuck on a question? Ask the AI Doubt Solver</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="bg-gray-50 p-2 rounded-md">
    <div className="flex items-center gap-1 text-gray-500">
      {icon}
      <span>{label}</span>
    </div>
    <div className="font-bold text-gray-900 text-base">{value}</div>
  </div>
);
